import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, DollarSign, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/pricing";
import api from "@/lib/api";
import { toast } from "sonner";

// Sent-RFP log: which carriers received each RFP + log the rates they quote back.
export default function RfpLog({ open, onOpenChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({}); // logId -> {carrier, rate, notes}
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/rfp-log")
      .then(({ data }) => setEntries(data))
      .catch(() => toast.error("Could not load RFP log"))
      .finally(() => setLoading(false));
  }, [open]);

  const upd = (id, k, v) => setForm((f) => ({ ...f, [id]: { ...f[id], [k]: v } }));

  const logReply = async (entry) => {
    const f = form[entry.id] || {};
    if (!f.carrier || !f.rate) {
      toast.error("Pick the carrier and enter the quoted rate");
      return;
    }
    setSaving(entry.id);
    try {
      const { data } = await api.post(`/rfp-log/${entry.id}/reply`, {
        carrier_name: f.carrier,
        rate: Number(f.rate),
        notes: f.notes || "",
      });
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, replies: [...(e.replies || []), data] } : e))
      );
      setForm((prev) => ({ ...prev, [entry.id]: { carrier: "", rate: "", notes: "" } }));
      toast.success("Rate logged");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log rate");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1C1917]">Sent RFPs & Carrier Replies</DialogTitle>
          <DialogDescription>Every RFP sent to carriers — log the rates they quote back.</DialogDescription>
        </DialogHeader>
        <div className="overflow-auto flex-1 space-y-3" data-testid="rfp-log-list">
          {loading && <p className="text-sm text-[#57534E]">Loading…</p>}
          {!loading && entries.length === 0 && (
            <p className="text-sm text-[#57534E]">No RFPs sent to carriers yet.</p>
          )}
          {entries.map((e) => (
            <div key={e.id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`rfp-log-${e.id}`}>
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-[#F97316] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#1C1917] truncate">
                    {e.route_summary || e.subject}
                  </div>
                  <div className="text-xs text-[#57534E]">
                    {new Date(e.sent_at).toLocaleString()} · Sent to:{" "}
                    <span className="font-medium text-[#1C1917]">
                      {(e.carriers || []).map((c) => c.name).join(", ")}
                    </span>
                  </div>
                </div>
              </div>
              {(e.replies || []).length > 0 && (
                <div className="space-y-1" data-testid={`rfp-replies-${e.id}`}>
                  {e.replies.map((r, i) => (
                    <div key={`${r.carrier_name}-${i}`} className="flex flex-wrap items-center gap-1.5 text-xs bg-[#FFF7ED] border border-[#F97316]/30 rounded px-2 py-1">
                      <DollarSign className="h-3 w-3 text-[#EA580C]" />
                      <span className="font-semibold">{r.carrier_name}</span> quoted{" "}
                      <span className="font-bold text-[#EA580C]">{fmtMoney(r.rate)}</span>
                      {r.notes && <span className="text-[#57534E]">— {r.notes}</span>}
                      <span className="text-stone-400">({new Date(r.logged_at).toLocaleDateString()})</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Select value={form[e.id]?.carrier || ""} onValueChange={(v) => upd(e.id, "carrier", v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`reply-carrier-${e.id}`}>
                    <SelectValue placeholder="Carrier…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(e.carriers || []).map((c) => (
                      <SelectItem key={c.id || c.name} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Rate $"
                  className="h-8 w-24 text-xs"
                  value={form[e.id]?.rate || ""}
                  onChange={(ev) => upd(e.id, "rate", ev.target.value)}
                  data-testid={`reply-rate-${e.id}`}
                />
                <Input
                  placeholder="Notes (optional)"
                  className="h-8 flex-1 min-w-[120px] text-xs"
                  value={form[e.id]?.notes || ""}
                  onChange={(ev) => upd(e.id, "notes", ev.target.value)}
                  data-testid={`reply-notes-${e.id}`}
                />
                <Button
                  size="sm"
                  className="h-8 text-xs bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
                  onClick={() => logReply(e)}
                  disabled={saving === e.id}
                  data-testid={`reply-save-${e.id}`}
                >
                  {saving === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Log rate"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
